import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { primaryColor, secondaryColor } from "../../../utils/comon";

const CustomSelect = ({
    id,
    label,
    options = [],
    value,
    onChange,
    placeholder = "Select",
    error,
    errorMessage,
    required = false,
    isMulti = false,
    isDisabled = false
}) => {
    const [open, setOpen] = useState(false);
    const [isAbove, setIsAbove] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, bottom: 0 });
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);
    const timeoutRef = useRef(null);
    const optionsRefs = useRef({});

    const updateCoords = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setCoords({
                top: rect.top + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
                bottom: rect.bottom + window.scrollY,
                rectBottom: rect.bottom
            });

            // Check if there is enough space below
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 250 && rect.top > spaceBelow) {
                setIsAbove(true);
            } else {
                setIsAbove(false);
            }
        }
    };

    useEffect(() => {
        if (open) {
            updateCoords();
            window.addEventListener("scroll", updateCoords, true);
            window.addEventListener("resize", updateCoords);
        }
        if (!open) {
            setSearchTerm("");
        }
        return () => {
            window.removeEventListener("scroll", updateCoords, true);
            window.removeEventListener("resize", updateCoords);
        };
    }, [open]);

    // Handle jumping to option when searchTerm changes
    useEffect(() => {
        if (open && searchTerm) {
            const match = options.find(opt =>
                opt.label.toLowerCase().startsWith(searchTerm.toLowerCase())
            );
            if (match && optionsRefs.current[match.value]) {
                optionsRefs.current[match.value].scrollIntoView({
                    behavior: "smooth",
                    block: "nearest"
                });
            }
        }
    }, [searchTerm, open, options]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target) &&
                dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleKeyDown = (e) => {
        if (isDisabled) return;
        if (e.key === "Tab") {
            if (open) setOpen(false);
            return;
        }

        if (!open) {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                setOpen(true);
            }
            return;
        }

        if (e.key === "Escape") {
            setOpen(false);
            return;
        }

        if (e.key === "Backspace") {
            setSearchTerm(prev => prev.slice(0, -1));
            return;
        }

        // Catch alphanumeric keys for jumping
        if (e.key.length === 1 && /[a-zA-Z0-9\s]/.test(e.key)) {
            const char = e.key.toLowerCase();
            setSearchTerm(prev => prev + char);

            // Reset search term after 1s of no typing
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                setSearchTerm("");
            }, 1000);
        }
    };

    const handleSelect = (option) => {
        if (isMulti) {
            const currentValues = Array.isArray(value) ? value : [];
            const isSelected = currentValues.some(v => v.value === option.value);
            let newValue;
            if (isSelected) {
                newValue = currentValues.filter(v => v.value !== option.value);
            } else {
                newValue = [...currentValues, option];
            }
            onChange(newValue);
        } else {
            onChange(option);
            setOpen(false);
        }
    };

    const getDisplayValue = () => {
        if (isMulti) {
            if (Array.isArray(value) && value.length > 0) {
                return value.map(v => v.label).join(", ");
            }
            return placeholder;
        }
        return value ? value.label : placeholder;
    };

    const isOptionSelected = (opt) => {
        if (isMulti) {
            return Array.isArray(value) && value.some(v => v.value === opt.value);
        }
        return value && value.value === opt.value;
    };

    const dropdownContent = open && (
        <div
            ref={dropdownRef}
            className={`fixed z-[99999] bg-white dark:bg-cardbg border border-[#E2E8F0] dark:border-bordercolor rounded-xl shadow-2xl overflow-y-auto max-h-[250px] custom-scrollbar`}
            style={{
                top: isAbove ? coords.top - 12 : coords.bottom + 12,
                left: coords.left,
                width: coords.width,
                transform: isAbove ? "translateY(-100%)" : "none"
            }}
        >
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            `}</style>
            <div className="py-2">
                {options.map((opt) => (
                    <div
                        key={opt.value}
                        ref={el => optionsRefs.current[opt.value] = el}
                        onClick={(e) => {
                            if (isMulti) e.stopPropagation();
                            handleSelect(opt);
                        }}
                        className={`px-4 py-2.5 text-[13px] cursor-pointer flex items-center justify-between transition-colors duration-150 ${isOptionSelected(opt) ? "font-normal" : "text-[#475569] dark:text-textsecondary hover:bg-gray-50 dark:hover:bg-actionitembg"}`}
                        style={isOptionSelected(opt) ? { backgroundColor: "var(--color-secondaryaccent)", color: "var(--color-primaryaccent)" } : {}}
                        onMouseEnter={(e) => {
                            if (!isOptionSelected(opt)) {
                                e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
                                e.currentTarget.style.color = "var(--color-primaryaccent)";
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isOptionSelected(opt)) {
                                e.currentTarget.style.backgroundColor = '';
                                e.currentTarget.style.color = '';
                            }
                        }}
                    >
                        <span>{opt.label}</span>
                        {isOptionSelected(opt) && (
                            <span style={{ color: "var(--color-primaryaccent)" }}>
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            </span>
                        )}
                    </div>
                ))}
                {options.length === 0 && (
                    <div className="px-4 py-3 text-xs text-gray-400 text-center italic">
                        No options available
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div
            id={id}
            className={`w-full relative outline-none ${isDisabled ? "opacity-60 pointer-events-none" : ""}`}
            ref={containerRef}
            onKeyDown={handleKeyDown}
            tabIndex={0}
        >
            {/* Label */}
            {label && (
                <label className={`text-xs font-semibold mb-1 block uppercase tracking-tight transition-colors ${error ? "text-red-500 dark:text-red-400 font-bold" : "text-[#0F172A] dark:text-textsecondary"}`}>
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}

            {/* Select Box */}
            <div
                onClick={() => !isDisabled && setOpen(!open)}
                className={`border rounded-md px-3 py-2 w-full text-sm cursor-pointer flex justify-between items-center transition-all duration-200 ${
                    error ? "border-red-500 bg-red-50/10 ring-1 ring-red-500/30" : "border-gray-200 dark:border-bordercolor hover:border-gray-400"
                }`}
            >
                <div className="flex items-center gap-2 overflow-hidden mr-2">
                    <span className={`truncate ${(isMulti ? (Array.isArray(value) && value.length > 0) : value) ? "text-textprimary" : "text-gray-400"}`}>
                        {getDisplayValue()}
                    </span>
                </div>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`text-gray-400 text-xs transition-transform duration-300 ${open ? "rotate-180" : ""
                        }`}
                />
            </div>

            {error && (
                <p className="text-red-500 text-[10px] mt-1 font-medium">
                    {typeof error === 'string' ? error : errorMessage}
                </p>
            )}

            {open && createPortal(dropdownContent, document.body)}
        </div>
    );
};

export default CustomSelect;
